<template>
  <div v-if="singleLine">
    <RefLinkParts
      v-if="showQr"
      showQr
      :url="url"
      :placeholderText="placeholderText"
      :qrSize="qrSize"
    />
    <RefLinkParts
      v-if="showUrl"
      showUrl
      showBtnCopy
      showBtnShare
      :shareBtnIcon="shareBtnIcon"
      :url="url"
      :placeholderText="placeholderText"
      btnSize="small"
      :urlSize="maxVisibleChars"
    />
  </div>
  <div v-else>
    <BasicCard
      :headerClass="headerClass"
      :title="title"
      :style="{ minHeight }"
    >
      <template #buttons>
        <RefLinkParts
          showBtnLink
          showBtnCopy
          showBtnEmail
          showBtnShare
          :shareBtnIcon="shareBtnIcon"
          :url="url"
          :placeholderText="placeholderText"
          btnSize="small"
        />
      </template>

      <slot name="customLayout" :url="url">
        <div class="d-flex">
          <div v-if="showQr" class="mr-4">
            <RefLinkParts
              showQr
              :url="url"
              :placeholderText="placeholderText"
              :qrSize="qrSize"
            />
          </div>
          <div>
            <slot :url="url" />

            <RefLinkParts
              v-if="showUrl"
              showUrl
              showBtnCopy
              showBtnShare
              :shareBtnIcon="shareBtnIcon"
              :url="url"
              :placeholderText="placeholderText"
              btnSize="small"
              :urlSize="maxVisibleChars"
            />
            <slot name="after" :url="url" />
          </div>
        </div>
      </slot>
    </BasicCard>
  </div>
</template>

<script lang="ts" setup>

import { useAccountPropertiesItem, useAwaitValue } from '@/composables';
import { TableGridItem } from '@/models/table-grid-interface';
import { runTemplate } from '@/utils/property-template-service';
import { computed, onMounted, ref } from 'vue';
import { RefLinkProps, defaults } from './RefLink.ts';

import BasicCard from './base/BasicCard.vue';
import RefLinkParts from './RefLinkParts.vue';


const props = withDefaults(
  defineProps<RefLinkProps>(), {
    id: undefined,
    headerClass: () => defaults.headerClass,
    template: () => defaults.template,
    singleLine: () => defaults.singleLine,
    title: () => defaults.title,
    placeholderText: () => defaults.placeholderText,
    maxVisibleChars: () => defaults.maxVisibleChars,
    qrSize: () => defaults.qrSize,
    showQr: () => defaults.showQr,
    showUrl: () => defaults.showUrl,
    shareBtnIcon: () => defaults.shareBtnIcon,
  });

const url = ref<string | undefined>(undefined);

const accountProps = ref<TableGridItem | null>();
const minHeight = computed(() => props.showQr ? props.qrSize : 0);

function extractTemplateTokens(template: string): string[] {
  const tokens = new Set<string>();
  const patterns = [ /\{\{([^}]+)\}\}/g, /~~([^~]+)~~/g, /__([^_]+)__/g ];
  for (const re of patterns) {
    let m: RegExpExecArray | null;
    while ((m = re.exec(template)) !== null) {
      if (m[1]) tokens.add(m[1]);
    }
  }
  return Array.from(tokens);
}

function hasUnresolvedTokens(s: string): boolean {
  return /\{\{[^}]+\}\}|~~[^~]+~~|__[^_]+__/.test(s);
}

function computeUrl(): string | undefined {
  // Expand system variables first
  const systemExpanded = runTemplate(props.template);

  // If we don't have account props yet, don't show URL
  if (!accountProps.value) return undefined;

  // Validate required item-based tokens exist and are non-empty
  const tokens = extractTemplateTokens(systemExpanded);
  for (const key of tokens) {
    const prop = accountProps.value[key as keyof TableGridItem];
    if (prop !== undefined) {
      const presentable = String(prop?.presentable ?? '');
      if (presentable.length === 0) {
        return undefined;
      }
    }
  }

  const resolved = runTemplate(props.template, accountProps.value);
  if (!resolved || resolved.trim().length === 0) return undefined;
  if (hasUnresolvedTokens(resolved)) return undefined;
  return resolved;
}

onMounted(async () => {
  accountProps.value = await useAwaitValue(useAccountPropertiesItem(), 'srcItem');
  url.value = computeUrl();
});


</script>
